import { useState, useLayoutEffect, useRef, useCallback, type RefObject, type CSSProperties } from 'react';

const GAP_PX = 4;
const VIEWPORT_MARGIN_PX = 8;

function getScrollParents(el: HTMLElement): HTMLElement[] {
  const result: HTMLElement[] = [];
  let parent = el.parentElement;
  while (parent) {
    const { overflowY, overflow } = getComputedStyle(parent);
    if (/(auto|scroll|overlay)/.test(overflowY) || /(auto|scroll|overlay)/.test(overflow)) {
      result.push(parent);
    }
    parent = parent.parentElement;
  }
  return result;
}

export interface UseDropdownPositionOptions {
  /** Ширина по контенту списка, а не по ширине триггера */
  widthByContent?: boolean;
  /** Выровнять по центру триггера по горизонтали */
  alignCenter?: boolean;
  /**
   * Элемент выпадающей панели (обёртка портала или хост OverlayScrollArea).
   * Если передан — выбирается сторона открытия (вниз / вверх) по свободному месту во вьюпорте.
   */
  dropdownRef?: RefObject<HTMLElement | null>;
}

function computeVerticalTop(
  triggerRect: DOMRect,
  dropdownHeight: number,
): { top: number; placement: 'top' | 'bottom' } {
  const vh = window.innerHeight;
  const availableBelow = vh - triggerRect.bottom - GAP_PX - VIEWPORT_MARGIN_PX;
  const availableAbove = triggerRect.top - GAP_PX - VIEWPORT_MARGIN_PX;
  const h = Math.max(dropdownHeight, 1);

  if (h <= availableBelow) {
    return { top: triggerRect.bottom + GAP_PX, placement: 'bottom' };
  }
  if (h <= availableAbove) {
    return { top: triggerRect.top - GAP_PX - h, placement: 'top' };
  }
  // Не помещается целиком ни сверху ни снизу. Раньше здесь был top: VIEWPORT_MARGIN_PX —
  // панель оказывалась у верха вьюпорта и перекрывала само поле-триггер (модалки, узкий экран).
  // Всегда якорим к нижней кромке триггера: список может обрезаться снизу, но поле остаётся кликабельным.
  return { top: triggerRect.bottom + GAP_PX, placement: 'bottom' };
}

/**
 * Вычисляет позицию для выпадающего списка в портале (position: fixed).
 * Используется, когда dropdown рендерится в document.body и не должен обрезаться overflow.
 */
function computeStyle(
  triggerEl: HTMLElement,
  options: UseDropdownPositionOptions,
): CSSProperties {
  const { widthByContent, alignCenter, dropdownRef } = options;
  const rect = triggerEl.getBoundingClientRect();
  const left = alignCenter ? rect.left + rect.width / 2 : rect.left;

  const dropdownEl = dropdownRef?.current ?? null;
  let top: number;
  let placement: 'top' | 'bottom';

  if (dropdownRef) {
    if (dropdownEl) {
      const dh = dropdownEl.getBoundingClientRect().height;
      if (dh > 0) {
        const v = computeVerticalTop(rect, dh);
        top = v.top;
        placement = v.placement;
      } else {
        top = rect.bottom + GAP_PX;
        placement = 'bottom';
      }
    } else {
      top = rect.bottom + GAP_PX;
      placement = 'bottom';
    }
  } else {
    top = rect.bottom + GAP_PX;
    placement = 'bottom';
  }

  const transformOrigin = dropdownRef ? (placement === 'top' ? 'bottom center' : 'top center') : undefined;

  const base: CSSProperties = {
    position: 'fixed',
    top,
    left,
    zIndex: 1100,
    ...(alignCenter && { transform: 'translateX(-50%)' }),
    ...(transformOrigin && { transformOrigin }),
  };
  if (widthByContent) {
    return base;
  }
  return {
    ...base,
    width: rect.width,
    minWidth: rect.width,
    maxWidth: Math.max(rect.width, 200),
  };
}

function applyVisibilityGate(
  computed: CSSProperties,
  dropdownRef: RefObject<HTMLElement | null> | undefined,
  ready: boolean,
): CSSProperties {
  if (!dropdownRef) return computed;
  if (!ready) {
    return {
      ...computed,
      opacity: 0,
      visibility: 'hidden',
      pointerEvents: 'none',
    };
  }
  return {
    ...computed,
    opacity: 1,
    visibility: 'visible',
    pointerEvents: 'auto',
  };
}

export function useDropdownPosition(
  triggerRef: RefObject<HTMLElement | null>,
  open: boolean,
  options?: UseDropdownPositionOptions,
): CSSProperties {
  const widthByContent = options?.widthByContent ?? false;
  const alignCenter = options?.alignCenter ?? false;
  const dropdownRef = options?.dropdownRef;
  const [style, setStyle] = useState<CSSProperties>(() => {
    if (open && triggerRef.current) {
      const computed = computeStyle(triggerRef.current, { widthByContent, alignCenter, dropdownRef });
      const d = dropdownRef?.current ?? null;
      // Не требуем height > 0: при hint/анимации первый кадр часто даёт dh === 0, ResizeObserver
      // не всегда приходит — панель оставалась visibility:hidden и «поиск не работает» при живом state.
      const ready = !dropdownRef || d != null;
      return applyVisibilityGate(computed, dropdownRef, ready);
    }
    return {};
  });
  const rafRef = useRef<number | null>(null);
  const measureRafRef = useRef<number | null>(null);

  const update = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const d = dropdownRef?.current ?? null;
    const ready = !dropdownRef || d != null;
    const computed = computeStyle(el, { widthByContent, alignCenter, dropdownRef });
    setStyle(applyVisibilityGate(computed, dropdownRef, ready));
  }, [triggerRef, widthByContent, alignCenter, dropdownRef]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) {
      setStyle({});
      return;
    }

    update();

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(document.documentElement);

    let dropdownResizeObserver: ResizeObserver | null = null;
    const attachDropdownObserver = () => {
      const d = dropdownRef?.current;
      if (!d || dropdownResizeObserver) return;
      dropdownResizeObserver = new ResizeObserver(update);
      dropdownResizeObserver.observe(d);
    };

    let pollFrames = 0;
    const pollUntilDropdownMounted = () => {
      attachDropdownObserver();
      update();
      if (!dropdownRef?.current && pollFrames++ < 24) {
        measureRafRef.current = requestAnimationFrame(pollUntilDropdownMounted);
      }
    };
    if (dropdownRef) {
      pollFrames = 0;
      pollUntilDropdownMounted();
    }

    const onScroll = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(update);
    };

    const scrollParents = getScrollParents(triggerRef.current);
    scrollParents.forEach((p) => p.addEventListener('scroll', onScroll, { passive: true }));
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', update);

    return () => {
      resizeObserver.disconnect();
      dropdownResizeObserver?.disconnect();
      scrollParents.forEach((p) => p.removeEventListener('scroll', onScroll));
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', update);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (measureRafRef.current) cancelAnimationFrame(measureRafRef.current);
    };
  }, [open, triggerRef, widthByContent, alignCenter, dropdownRef, update]);

  return style;
}
